import { HTTP_STATUS } from "@milqit/messages";
import { ADMIN_ROLES, type AdminUser } from "@milqit/shared-types";
import type { AdminLoginValues } from "@milqit/validations";
import { z } from "zod";

/**
 * The dashboard's sign-in state: one store that route loaders, React (through `AuthProvider`)
 * and the API client all read, so they can never disagree.
 *
 * The real session is the API's httpOnly cookie. The copy kept here only drives the UI (route
 * guard, user menu, which buttons show); the API still refuses anything the cookie doesn't allow.
 * Because the copy can go stale (cookie expired, account changed by an owner, signed out in another
 * tab), it is checked against the API once per page load, cleared on the first 401, re-read when
 * another tab changes it, and dropped at its expiry time.
 */

export const SESSION_STORAGE_KEY = "milqit_admin_session";

const sessionSchema = z.object({
  email: z.string().min(1),
  name: z.string().min(1),
  role: z.enum(ADMIN_ROLES),
  /** Expiry, milliseconds since epoch. */
  expiresAt: z.number().int().positive(),
});
export type AdminSession = z.infer<typeof sessionSchema>;

/** Why the dashboard is signed out: the login page explains an expiry, not a deliberate sign-out. */
export type SignedOutReason = "signed-out" | "expired";

export type AuthState =
  | { status: "signed-in"; session: AdminSession }
  | { status: "signed-out"; reason: SignedOutReason | null };

export type LoginResult = { ok: true; session: AdminSession } | { ok: false; error: string };

/** The three calls the store needs; `auth-api.ts` makes them against the API. */
export interface AuthApi {
  login(values: AdminLoginValues): Promise<AdminUser>;
  /** The admin the session cookie belongs to; rejects with a 401 `HttpError` when there is none. */
  me(): Promise<AdminUser>;
  logout(): Promise<void>;
}

/** What the store needs to know about a failed request, so it doesn't depend on the API client. */
export interface HttpError {
  status: number;
  message: string;
}
const isHttpError = (error: unknown): error is HttpError =>
  typeof error === "object" &&
  error !== null &&
  typeof (error as { status?: unknown }).status === "number" &&
  typeof (error as { message?: unknown }).message === "string";

export interface AuthStoreOptions {
  api: AuthApi;
  /** Where "keep me signed in" keeps the session (shared by tabs), and where it otherwise goes. */
  persistent: Storage | null;
  temporary: Storage | null;
  /** How long a session lasts without "keep me signed in"; the API uses the same rule. */
  sessionTtlMs: number;
  /** Whether to confirm the stored copy with the API. */
  verifyWithServer: boolean;
  now?: () => number;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
}

export const LOGIN_ERRORS = {
  invalid: "That email and password don't match an admin account.",
  rateLimited: "Too many sign-in attempts. Wait a minute, then try again.",
  offline: "Couldn’t reach the server. Check your connection and try again.",
} as const;

/** "Keep me signed in" triples the session, matching the API's cookie. */
const REMEMBER_MULTIPLIER = 3;
/** setTimeout fires immediately for delays past ~24.8 days; re-check before then instead. */
const MAX_TIMER_MS = 2_147_483_647;

export function createAuthStore({
  api,
  persistent,
  temporary,
  sessionTtlMs,
  verifyWithServer,
  now = Date.now,
  setTimer = (callback, ms) => setTimeout(callback, ms),
  clearTimer = (timer) => clearTimeout(timer as ReturnType<typeof setTimeout>),
}: AuthStoreOptions) {
  const listeners = new Set<() => void>();
  let expiryTimer: unknown = null;
  let verification: Promise<AuthState> | null = null;

  /** Storage can throw (private mode, blocked site data); a failure just means "no session". */
  function read(storage: Storage | null): AdminSession | null {
    try {
      const raw = storage?.getItem(SESSION_STORAGE_KEY);
      if (!raw) return null;
      const parsed = sessionSchema.safeParse(JSON.parse(raw));
      if (parsed.success && parsed.data.expiresAt > now()) return parsed.data;
      storage?.removeItem(SESSION_STORAGE_KEY);
      return null;
    } catch {
      return null;
    }
  }

  function clearStorage() {
    for (const storage of [persistent, temporary]) {
      try {
        storage?.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // Nothing stored, or storage unavailable: either way there's nothing to clear.
      }
    }
  }

  function write(session: AdminSession, remember: boolean) {
    clearStorage();
    try {
      (remember ? persistent : temporary)?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Without storage the session lasts until the next full page load.
    }
  }

  const readStored = () => read(temporary) ?? read(persistent);
  const stateFrom = (session: AdminSession | null, reason: SignedOutReason | null): AuthState =>
    session ? { status: "signed-in", session } : { status: "signed-out", reason };

  let state: AuthState = stateFrom(readStored(), null);

  function scheduleExpiry() {
    if (expiryTimer !== null) clearTimer(expiryTimer);
    expiryTimer = null;
    if (state.status !== "signed-in") return;
    const remaining = state.session.expiresAt - now();
    expiryTimer = setTimer(
      () => {
        expiryTimer = null;
        // A long session is re-checked rather than expired early.
        if (remaining > MAX_TIMER_MS) scheduleExpiry();
        else expire();
      },
      Math.max(0, Math.min(remaining, MAX_TIMER_MS)),
    );
  }

  /** The single place state changes: storage, timers and listeners all follow from here. */
  function set(next: AuthState) {
    state = next;
    if (next.status === "signed-out") {
      clearStorage();
      verification = null;
    }
    scheduleExpiry();
    for (const listener of listeners) listener();
  }

  function expire() {
    if (state.status === "signed-in") set({ status: "signed-out", reason: "expired" });
  }

  scheduleExpiry();

  return {
    getState: (): AuthState => state,

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    async signIn(values: AdminLoginValues): Promise<LoginResult> {
      try {
        const user = await api.login(values);
        const remember = values.remember === true;
        const session: AdminSession = {
          email: user.email,
          name: user.name,
          role: user.role,
          expiresAt: now() + sessionTtlMs * (remember ? REMEMBER_MULTIPLIER : 1),
        };
        write(session, remember);
        // The API just confirmed this session, so this page load needn't ask again.
        verification = Promise.resolve<AuthState>({ status: "signed-in", session });
        set({ status: "signed-in", session });
        return { ok: true, session };
      } catch (error) {
        if (!isHttpError(error)) return { ok: false, error: LOGIN_ERRORS.offline };
        if (error.status === HTTP_STATUS.UNAUTHORIZED)
          return { ok: false, error: LOGIN_ERRORS.invalid };
        if (error.status === HTTP_STATUS.TOO_MANY_REQUESTS)
          return { ok: false, error: LOGIN_ERRORS.rateLimited };
        // Anything else carries a message written for staff (validation, server errors).
        return { ok: false, error: error.message };
      }
    },

    /** Signs out here first, so the UI never waits on the network; the cookie is cleared after. */
    async signOut() {
      const wasSignedIn = state.status === "signed-in";
      set({ status: "signed-out", reason: "signed-out" });
      if (wasSignedIn) await api.logout().catch(() => undefined);
    },

    /** The API refused the session (401): show the sign-in page with an explanation. */
    expire,

    /**
     * Confirms the stored copy with the API once per page load, and picks up a changed name or
     * role. A network failure keeps the copy: the next request will tell if it's really gone.
     */
    verify(): Promise<AuthState> {
      if (!verifyWithServer || state.status !== "signed-in") return Promise.resolve(state);
      verification ??= api.me().then(
        (user) => {
          if (state.status !== "signed-in") return state;
          const session = { ...state.session, name: user.name, role: user.role };
          const remember = read(persistent) !== null;
          write(session, remember);
          set({ status: "signed-in", session });
          return state;
        },
        (error: unknown) => {
          if (isHttpError(error) && error.status === HTTP_STATUS.UNAUTHORIZED) expire();
          else verification = null;
          return state;
        },
      );
      return verification;
    },

    /** Another tab signed in or out: follow it. */
    syncFromStorage() {
      const stored = readStored();
      if (stored && (state.status !== "signed-in" || stored.email !== state.session.email)) {
        set({ status: "signed-in", session: stored });
      } else if (!stored && state.status === "signed-in") {
        set({ status: "signed-out", reason: "signed-out" });
      }
    },
  };
}

export type AuthStore = ReturnType<typeof createAuthStore>;
